<h2>URL Detail</h2>
<p><strong>URL:</strong> {{ url }}</p>

<!-- DataForSEO Test Section -->
<div class="dataforseo-section">
  <h3>DataForSEO Analysis</h3>
  
  <button 
    (click)="fetchDataForSEO()" 
    [disabled]="dataForSEOStatus === 'loading'"
    class="btn-primary fetch-button">
    {{ dataForSEOStatus === 'loading' ? 'Testing...' : 'Fetch DataForSEO' }}
  </button>

  <div *ngIf="dataForSEOStatus !== 'idle'" class="status-message" [class]="dataForSEOStatus">
    {{ dataForSEOMessage }}
  </div>
</div>

<!-- Real Analysis Data -->
<div class="analysis-section">
  <h3>URL Analysis</h3>
  
  <!-- Loading State -->
  <div *ngIf="analysisStatus === 'loading'" class="loading">
    <p>Loading analysis data...</p>
  </div>

  <!-- Error State -->
  <div *ngIf="analysisStatus === 'error'" class="error">
    <p>Failed to load analysis data. This URL may not have been analyzed yet.</p>
  </div>

  <!-- Success State -->
  <div *ngIf="analysisStatus === 'success' && analysisData" class="analysis-data">
    <!-- Core Metrics -->
    <div class="metrics-grid">
      <div class="metric">
        <h4>OnPage Score</h4>
        <div class="score" [class]="getScoreClass(analysisData.onpageScore)">
          {{ analysisData.onpageScore }}
        </div>
      </div>
      <div class="metric">
        <h4>Word Count</h4>
        <div class="value">{{ analysisData.wordCount.toLocaleString() }}</div>
      </div>
      <div class="metric">
        <h4>Status Code</h4>
        <div class="value">{{ analysisData.statusCode }}</div>
      </div>
    </div>

    <!-- Issues & Warnings -->
    <div class="issues-section" *ngIf="analysisData.criticalIssues.length > 0 || analysisData.warnings.length > 0">
      <div class="issues-header" (click)="toggleIssuesSection()">
        <h4>Issues & Warnings ({{ analysisData.criticalIssues.length + analysisData.warnings.length }})</h4>
        <span class="chevron" [class.collapsed]="issuesSectionCollapsed">▼</span>
      </div>
      
      <div class="issues-content" [class.collapsed]="issuesSectionCollapsed">
        <div *ngIf="analysisData.criticalIssues.length > 0" class="critical-issues">
          <h5>Critical Issues ({{ analysisData.criticalIssues.length }})</h5>
          <ul>
            <li *ngFor="let issue of analysisData.criticalIssues">{{ issue }}</li>
          </ul>
        </div>
        
        <div *ngIf="analysisData.warnings.length > 0" class="warnings">
          <h5>Warnings ({{ analysisData.warnings.length }})</h5>
          <ul>
            <li *ngFor="let warning of analysisData.warnings">{{ warning }}</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Page Content Section -->
    <div class="content-section" *ngIf="hasContent()">
      <div class="content-header" (click)="toggleContentSection()">
        <h4>Page Content ({{ contentElements.length }} elements)</h4>
        <span class="chevron" [class.collapsed]="contentSectionCollapsed">▼</span>
      </div>
      
      <div class="content-body" [class.collapsed]="contentSectionCollapsed">
        <div class="content-info">
          <span>Key content elements extracted from the page</span>
        </div>
        
        <div class="content-elements">
          <div 
            *ngFor="let element of contentElements; trackBy: trackByElement" 
            class="content-element"
            [class]="element.type">
            
            <!-- Headings -->
            <div *ngIf="element.type === 'heading'" class="heading-element">
              <span class="heading-tag">H{{ element.level }}</span>
              <span class="heading-text" [class]="getHeadingClass(element.level!)">
                {{ element.content }}
              </span>
            </div>
            
            <!-- Text Content -->
            <div *ngIf="element.type === 'text'" class="text-element">
              <span class="text-content">{{ element.content }}</span>
            </div>
            
            <!-- Links -->
            <div *ngIf="element.type === 'link'" class="link-element">
              <span class="link-text">{{ element.content }}</span>
              <span class="link-url">{{ element.href }}</span>
            </div>
            
            <!-- Tables -->
            <div *ngIf="element.type === 'table'" class="table-element">
              <div class="table-header">{{ element.content }}</div>
              <div class="table-content">
                <div class="table-row">
                  <div 
                    *ngFor="let cell of element.tableData; trackBy: trackByElement" 
                    class="table-cell"
                    [class.header-cell]="cell.isHeader">
                    <span class="cell-text">{{ cell.text }}</span>
                    <span *ngIf="cell.urls && cell.urls.length > 0" class="cell-links">
                      <a *ngFor="let url of cell.urls" [href]="url.url" target="_blank" class="cell-link">
                        {{ url.anchor_text || url.url }}
                      </a>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Technical Metrics -->
    <div class="technical-metrics">
      <h4>Technical Performance</h4>
      <div class="metrics-grid">
        <div class="metric">
          <span>Time to Interactive:</span>
          <span>{{ analysisData.timeToInteractive }}ms</span>
        </div>
        <div class="metric">
          <span>Largest Contentful Paint:</span>
          <span>{{ analysisData.largestContentfulPaint }}ms</span>
        </div>
        <div class="metric">
          <span>First Input Delay:</span>
          <span>{{ analysisData.firstInputDelay }}ms</span>
        </div>
      </div>
    </div>

    <!-- Content Structure -->
    <div class="content-structure">
      <h4>Content Structure</h4>
      <div class="metrics-grid">
        <div class="metric">
          <span>H1 Tags:</span>
          <span>{{ analysisData.h1Count }}</span>
        </div>
        <div class="metric">
          <span>H2 Tags:</span>
          <span>{{ analysisData.h2Count }}</span>
        </div>
        <div class="metric">
          <span>H3 Tags:</span>
          <span>{{ analysisData.h3Count }}</span>
        </div>
        <div class="metric">
          <span>Internal Links:</span>
          <span>{{ analysisData.internalLinksCount }}</span>
        </div>
        <div class="metric">
          <span>External Links:</span>
          <span>{{ analysisData.externalLinksCount }}</span>
        </div>
      </div>
    </div>

    <!-- Internal Links Section -->
    <div class="links-section" *ngIf="hasLinks()">
      <div class="links-header" (click)="toggleLinksSection()">
        <h4>Internal Links ({{ getLinkStats().total }})</h4>
        <span class="chevron" [class.collapsed]="linksSectionCollapsed">▼</span>
      </div>
      
      <div class="links-body" [class.collapsed]="linksSectionCollapsed">
        <!-- Link Statistics -->
        <div class="links-stats">
          <div class="stat-card">
            <div class="stat-value">{{ getLinkStats().structural }}</div>
            <div class="stat-label">Structural</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ getLinkStats().contextual }}</div>
            <div class="stat-label">Contextual</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ getLinkStats().samePage }}</div>
            <div class="stat-label">Same Page</div>
          </div>
          <div class="stat-card" *ngIf="getLinkStats().unknown > 0">
            <div class="stat-value">{{ getLinkStats().unknown }}</div>
            <div class="stat-label">Unknown</div>
          </div>
        </div>

        <!-- Filters and Sort -->
        <div class="links-controls">
          <div class="filter-group">
            <label>Filter:</label>
            <select [(ngModel)]="linkFilter" class="filter-select">
              <option value="all">All Links</option>
              <option value="structural">Structural</option>
              <option value="contextual">Contextual</option>
              <option value="same_page">Same Page</option>
            </select>
          </div>
          <div class="filter-group">
            <label>Sort:</label>
            <select [(ngModel)]="linkSort" class="filter-select">
              <option value="type">By Type</option>
              <option value="confidence">By Confidence</option>
              <option value="anchor">By Anchor Text</option>
              <option value="url">By URL</option>
            </select>
          </div>
        </div>

        <!-- Links List -->
        <div class="links-list">
          <div 
            *ngFor="let link of getFilteredLinks(); trackBy: trackByLink" 
            class="link-item"
            [class.expanded]="link._expanded">
            
            <div class="link-header" (click)="toggleLinkDetails(link)">
              <div class="link-main">
                <span class="link-badge" [class]="getLinkTypeClass(link.linkType)">
                  {{ getLinkTypeLabel(link.linkType) }}
                </span>
                <span class="link-anchor-text">
                  {{ link.anchorText || '(no anchor text)' }}
                </span>
              </div>
              <div class="link-meta">
                <span class="link-confidence" [class]="getConfidenceClass(link.confidence)">
                  {{ (link.confidence * 100).toFixed(0) }}%
                </span>
                <span class="link-chevron" [class.rotated]="link._expanded">▼</span>
              </div>
            </div>

            <div class="link-url">
              <a [href]="link.targetUrl" target="_blank" (click)="$event.stopPropagation()">
                {{ link.targetRelativeUrl }}
              </a>
              <span class="link-flags">
                <span *ngIf="!link.dofollow" class="flag flag-nofollow">nofollow</span>
                <span *ngIf="link.isBroken" class="flag flag-broken">broken</span>
              </span>
            </div>

            <!-- Expandable Details -->
            <div class="link-details" *ngIf="link._expanded">
              <div class="detail-grid">
                <div class="detail-item" *ngIf="link.classificationSignals?.linkFrequency !== undefined">
                  <span class="detail-label">Frequency:</span>
                  <span class="detail-value">
                    {{ link.classificationSignals.linkFrequency }} pages
                    <span *ngIf="link.classificationSignals.linkFrequencyRatio !== undefined">
                      ({{ (link.classificationSignals.linkFrequencyRatio * 100).toFixed(1) }}%)
                    </span>
                  </span>
                </div>
                <div class="detail-item" *ngIf="link.classificationSignals?.anchorTextLength !== undefined">
                  <span class="detail-label">Anchor Length:</span>
                  <span class="detail-value">{{ link.classificationSignals.anchorTextLength }} chars</span>
                </div>
                <div class="detail-item" *ngIf="link.classificationSignals?.urlPattern">
                  <span class="detail-label">URL Pattern:</span>
                  <span class="detail-value">{{ link.classificationSignals.urlPattern }}</span>
                </div>
                <div class="detail-item" *ngIf="link.classificationSignals?.isGenericAnchor !== undefined">
                  <span class="detail-label">Generic Anchor:</span>
                  <span class="detail-value">{{ link.classificationSignals.isGenericAnchor ? 'Yes' : 'No' }}</span>
                </div>
                <div class="detail-item" *ngIf="link.classificationSignals?.isSamePage !== undefined">
                  <span class="detail-label">Same Page:</span>
                  <span class="detail-value">{{ link.classificationSignals.isSamePage ? 'Yes' : 'No' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="links-info" *ngIf="getFilteredLinks().length === 0">
          <p>No links match the current filter.</p>
        </div>
      </div>
    </div>

    <!-- Analysis Date -->
    <div class="analysis-date">
      <p><em>Analyzed on: {{ analysisData.analyzedAt | date:'medium' }}</em></p>
      <p *ngIf="analysisData.links?.fetchedAt"><em>Links fetched on: {{ analysisData.links.fetchedAt | date:'medium' }}</em></p>
    </div>
  </div>
</div> 